import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, FileText, Calendar, Shield, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';

const TAG_FIELD_BY_TYPE = {
  officer: "tagged_officers",
  artifact: "tagged_artifacts",
  ritual: "tagged_rituals",
  policy: "tagged_policies",
  capability: "tagged_capabilities",
};

function getTagField(tagType) {
  return TAG_FIELD_BY_TYPE[tagType];
}

const typeIcons = {
  officer: Users,
  artifact: FileText,
  ritual: Calendar,
  policy: Shield,
  capability: Zap
};

const typeColors = {
  officer: 'bg-cyan-500',
  artifact: 'bg-purple-500',
  ritual: 'bg-indigo-500',
  policy: 'bg-amber-500',
  capability: 'bg-emerald-500'
};

const pluralMap = {
  officer: 'officers',
  artifact: 'artifacts',
  ritual: 'rituals',
  policy: 'policies',
  capability: 'capabilities',
};

export default function IssueTagsCard({ reportIssues, progressReports, activities, artifacts, rituals, policies, capabilities, onTagClick, officers }) {
  const [showFullList, setShowFullList] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [showActivities, setShowActivities] = useState(false);

  // Build tag data: group by tag, count unique activities
  const tagActivityCounts = React.useMemo(() => {
    const counts = {};
    const existingActivityIds = new Set(activities.map(a => a.id));

    reportIssues.forEach(reportIssue => {
      const report = progressReports.find(r => r.id === reportIssue.progress_report_id);
      if (!report || !existingActivityIds.has(report.activity_id)) return;

      // Process each tag type
      ['officer', 'artifact', 'ritual', 'policy', 'capability'].forEach(tagType => {
        const tagField = getTagField(tagType);
        if (!tagField) return;

        const tags = reportIssue[tagField] || [];
        tags.forEach(tagValue => {
          const key = `${tagType}:::${tagValue}`;
          
          if (!counts[key]) {
            // Resolve tag name
            let tagName = tagValue;
            let extraData = {};

            if (tagType === 'officer') {
              const officer = officers.find(o => o.user_email === tagValue);
              tagName = officer?.callsign || tagValue.split('@')[0];
              if (officer) {
                extraData = {
                  rank: officer.rank_title,
                  level: Math.floor((officer.total_xp || 0) / 500) + 1
                };
              }
            } else if (tagType === 'artifact') {
              const art = artifacts.find(a => a.id === tagValue || a.name === tagValue);
              tagName = art?.name || tagValue;
            } else if (tagType === 'ritual') {
              const ritual = rituals.find(r => r.id === tagValue || r.name === tagValue);
              tagName = ritual?.name || tagValue;
            } else if (tagType === 'policy') {
              const policy = policies.find(p => p.id === tagValue || p.title === tagValue);
              tagName = policy?.title || tagValue;
            } else if (tagType === 'capability') {
              const cap = capabilities.find(c => c.id === tagValue || c.name === tagValue);
              tagName = cap?.name || tagValue;
            }

            counts[key] = {
              type: tagType,
              value: tagValue,
              name: tagName,
              activityIds: new Set(),
              ...extraData
            };
          }

          counts[key].activityIds.add(report.activity_id);
        });
      });
    });

    // Convert to array and sort by activity count
    return Object.values(counts)
      .map(item => ({
        ...item,
        count: item.activityIds.size,
        activityIds: Array.from(item.activityIds)
      }))
      .sort((a, b) => b.count - a.count);
  }, [reportIssues, progressReports, artifacts, rituals, policies, capabilities, officers]);

  const topTags = tagActivityCounts.slice(0, 10);
  const otherTags = tagActivityCounts.slice(10);
  const otherCount = otherTags.reduce((sum, tag) => sum + tag.count, 0);
  const maxCount = Math.max(...topTags.map(t => t.count), otherCount || 0);

  const getActivitiesForTag = (tag) => {
    return activities.filter(a => tag.activityIds.includes(a.id));
  };

  const handleTagClick = (tag) => {
    setSelectedTag(tag);
    setShowActivities(true);
  };

  return (
    <>
      <Card 
        className="glass-panel border-none overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => setShowFullList(true)}
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-slate-700 text-lg font-semibold tracking-tight flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-400" />
            Tags by Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {topTags.map((tag) => {
              const Icon = typeIcons[tag.type];
              const unitLabel = tag.count === 1 ? 'activity' : 'activities';
              
              return (
                <div 
                  key={`${tag.type}:::${tag.value}`}
                  className="space-y-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTagClick(tag);
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Icon className={`w-4 h-4 ${typeColors[tag.type].replace('bg-', 'text-')} flex-shrink-0`} />
                      {tag.type === 'officer' ? (
                        <div className="flex flex-col min-w-0">
                          <span className="text-xs text-slate-700 font-medium truncate">{tag.name}</span>
                          {tag.rank && (
                            <span className="text-[10px] text-slate-500 truncate">
                              {tag.rank} • Lvl {tag.level}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-600 truncate">{tag.name}</span>
                      )}
                    </div>
                    <span className="text-xs font-semibold text-slate-700 flex-shrink-0 whitespace-nowrap">
                      {tag.count} {unitLabel}
                    </span>
                  </div>
                  <div 
                    className="h-5 bg-slate-200 rounded overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      onTagClick?.({
                        tagType: `tagged_${pluralMap[tag.type] ?? `${tag.type}s`}`,
                        tagValue: tag.value
                      });
                    }}
                  >
                    <div 
                      className={`h-full ${typeColors[tag.type]} transition-all`}
                      style={{ width: `${(tag.count / maxCount) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
            
            {otherTags.length > 0 && (
              <div 
                className="space-y-1"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullList(true);
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-600">Other ({otherTags.length})</span>
                  <span className="text-xs font-semibold text-slate-700 flex-shrink-0">{otherCount} activities</span>
                </div>
                <div 
                  className="h-5 bg-slate-200 rounded overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFullList(true);
                  }}
                >
                  <div 
                    className="h-full bg-slate-400 transition-all"
                    style={{ width: `${(otherCount / maxCount) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Full List Dialog */}
      <Dialog open={showFullList} onOpenChange={setShowFullList}>
        <DialogContent data-testid="dash-issuetagscard-dialog" className="bg-white border-slate-200 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>All Tagged Issues ({tagActivityCounts.length})</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {tagActivityCounts.map((tag) => {
              const Icon = typeIcons[tag.type];
              const unitLabel = tag.count === 1 ? 'activity' : 'activities';
              
              return (
                <div
                  key={`${tag.type}:::${tag.value}`}
                  className="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer"
                  onClick={() => {
                    handleTagClick(tag);
                    setShowFullList(false);
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1">
                           <Icon className={`w-4 h-4 ${typeColors[tag.type].replace('bg-', 'text-')} flex-shrink-0`} />
                           <div className="flex-1">
                             <div className="text-sm font-medium text-slate-900">{tag.name}</div>
                             <div className="text-xs text-slate-500">
                               <Badge variant="outline" className="text-xs capitalize mt-1">
                                 {tag.type}
                               </Badge>
                             </div>
                           </div>
                         </div>
                         <div className="text-right flex-shrink-0">
                           <div className="text-sm font-semibold text-slate-900">{tag.count}</div>
                           <div className="text-xs text-slate-500">{unitLabel}</div>
                           <Button 
                             size="sm" 
                             variant="ghost" 
                             className="mt-1 h-7 text-xs"
                             onClick={(e) => {
                               e.stopPropagation();
                               onTagClick?.({
                                 tagType: `tagged_${pluralMap[tag.type] ?? `${tag.type}s`}`,
                                 tagValue: tag.value
                               });
                               setShowFullList(false);
                             }}
                           >
                             History
                           </Button>
                         </div>
                       </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Activities Dialog */}
      <Dialog open={showActivities} onOpenChange={setShowActivities}>
        <DialogContent data-testid="dash-issuetagscard-dialog-1" className="bg-white border-slate-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-400" />
              Activities Tagged with "{selectedTag?.name}"
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {selectedTag && getActivitiesForTag(selectedTag).length > 0 ? (
              getActivitiesForTag(selectedTag).map((activity) => (
                <Link
                  key={activity.id}
                  to={createPageUrl('activities')}
                  onClick={() => setShowActivities(false)}
                >
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-indigo-400 hover:shadow-md transition-all">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900">
                          [{activity.activity_code}] {activity.title}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {activity.responsible_email?.split('@')[0]}
                        </div>
                      </div>
                      <Badge 
                        variant="outline"
                        className={
                          activity.key_results_status === 'green' ? 'border-emerald-500 text-emerald-600' :
                          activity.key_results_status === 'red' ? 'border-red-500 text-red-600' :
                          'border-amber-500 text-amber-600'
                        }
                      >
                        {activity.key_results_status || 'yellow'}
                      </Badge>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="text-center py-8 text-slate-500 text-sm">
                No activities found
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}